import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTable, useSortBy, usePagination, useFilters } from "react-table";
import SkillService from "../Service/SkillService";

const SMESkillManagementPage = () => {
  const location = useLocation();
  const navigate = useHistory();
  const user = location.state?.user;

  const [allSkillNames, setAllSkillNames] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [skills, setSkills] = useState([]);
  const [newSkill, setNewSkill] = useState({
    smeSkillMappingId: null,
    skillId: null,
    skillName: "",
    rating: "0",
    track: ""
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);

  const [pageIndexForNewSkill, setPageIndexForNewSkill] = useState(null);

  useEffect(() => {
    fetchExistingSkills();
    fetchAllTracks();

    if (user?.track) {
      setNewSkill(prev => ({
        ...prev,
        track: user.track
      }));
      fetchSkillsByTrack(user.track);
    }
  }, []);

  const fetchExistingSkills = async () => {
    try {
      const response = await SkillService.getSkillsByUserId(user.userId);
      const normalized = response.map((s) => ({
        ...s,
        track: s.track || "-",
        smeSkillMappingId: s.smeSkillMappingId
      }));
      setSkills(normalized);
    } catch (err) {
      console.error("Failed to fetch SME skills:", err);
      setError("Failed to fetch SME skills");
    }
  };

  const fetchAllTracks = async () => {
    try {
      const tracksResponse = await SkillService.getAllTracks();
      setTracks(tracksResponse || []);
    } catch {
      setError("Failed to fetch tracks");
    }
  };

  const fetchSkillsByTrack = async (track) => {
    try {
      const response = await SkillService.getAllSkillsByTrack(track);
      setAllSkillNames(response || []);
    } catch {
      setError("Failed to fetch skill suggestions");
    }
  };

  const handleTrackChange = async (e) => {
    const selectedTrack = e.target.value;
    setNewSkill({ ...newSkill, track: selectedTrack, skillId: null, skillName: "" });
    await fetchSkillsByTrack(selectedTrack);
  };

  const handleSkillNameChange = (e) => {
    const input = e.target.value;
    const matched = allSkillNames.find(skill => skill.skillName === input);
    if (matched) {
      setNewSkill({ ...newSkill, skillName: matched.skillName, skillId: matched.skillId });
    } else {
      setNewSkill({ ...newSkill, skillName: input, skillId: null });
    }
  };

  const handleAddOrUpdateSkill = async () => {
    if (!newSkill.skillName || !newSkill.track) {
      alert("Track and Skill name are required");
      return;
    }

    try {
      const payload = {
        smeSkillMappingId: newSkill.smeSkillMappingId,
        userId: user.userId,
        skillId: newSkill.skillId,
        skillName: newSkill.skillName,
        rating: newSkill.rating,
        track: newSkill.track,
      };

      if (editingIndex !== null) {
        const updated = await SkillService.updateSMESkillRating(payload);
        setSkills(prev => {
          const clone = [...prev];
          clone[editingIndex] = { ...updated, track: newSkill.track };
          return clone;
        });
      } else {
        const saved = await SkillService.mapSkillToSME(payload);
        setSkills(prev => {
          const updatedSkills = [...prev, { ...saved, track: newSkill.track }];
          const newPageIndex = Math.floor(updatedSkills.length / pageSize);
          setPageIndexForNewSkill(newPageIndex);
          return updatedSkills;
        });

      }

      resetForm();
    } catch (err) {
      alert("Operation failed: " + err.message);
    }
  };

  const handleEdit = (index) => {
    const skill = skills[index];
    setNewSkill({
      smeSkillMappingId: skill.smeSkillMappingId || null,
      skillId: skill.skillId || null,
      skillName: skill.skillName,
      rating: skill.rating,
      track: skill.track,
    });
    setEditingIndex(index);
    fetchSkillsByTrack(skill.track);
  };

  const handleDelete = async (index) => {
    const skill = skills[index];
    const confirmed = window.confirm(`Are you sure you want to delete the skill "${skill.skillName}" under track "${skill.track}"?`);
    if (!confirmed) return;

    if (!skill.smeSkillMappingId) {
      alert("Skill mapping ID is missing.");
      return;
    }

    try {
      await SkillService.deleteSMESkillMapping(skill.smeSkillMappingId);
      const updatedSkills = [...skills];
      updatedSkills.splice(index, 1);
      setSkills(updatedSkills);
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed: " + err.message);
    }
  };

  const resetForm = () => {
    setNewSkill({
      smeSkillMappingId: null,
      skillId: null,
      skillName: "",
      rating: "0",
      track: user?.track || ""
    });
    setEditingIndex(null);
    fetchSkillsByTrack(user?.track || "");
  };

  const filteredSkills = useMemo(() => {
    if (!searchTerm) return skills;
    return skills.filter((skill) =>
      skill.skillName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [skills, searchTerm]);


  const columns = useMemo(
    () => [
      { Header: "Track", accessor: "track" },
      { Header: "Skill Name", accessor: "skillName" },
      { Header: "Rating", accessor: "rating" },
      {
        Header: "Actions",
        accessor: "Actions",
        Cell: ({ row }) => {
          const index = skills.findIndex(skill => skill.smeSkillMappingId === row.original.smeSkillMappingId);
          return (
            <>
              <button
                onClick={() => handleEdit(index)}
                className="px-3 py-1 bg-yellow-400 text-white rounded text-sm mr-2"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(index)}
                className="px-3 py-1 bg-red-500 text-white rounded text-sm"
              >
                Delete
              </button>
            </>
          );
        },
        disableSortBy: true,
      }
    ],
    [skills]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredSkills,
      initialState: { pageIndex: 0, pageSize: 5 },
      autoResetPage: false,
    },
    useFilters,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    gotoPage(0);
  }, [searchTerm, gotoPage]);

  // Navigate to page after new skill added
  useEffect(() => {
    if (pageIndexForNewSkill !== null) {
      gotoPage(pageIndexForNewSkill);
      setPageIndexForNewSkill(null);
    }
  }, [pageIndexForNewSkill, gotoPage]);

  const filteredSkillSuggestions = useMemo(() => {
    const addedSkillNames = new Set(skills.map(skill => skill.skillName.toLowerCase()));
    return allSkillNames.filter(skill =>
      !addedSkillNames.has(skill.skillName.toLowerCase())
    );
  }, [allSkillNames, skills]);

  return (
    <div className="border border-blue-900 p-6 max-w-7xl mx-auto font-sans">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl font-bold mb-4">SME Skill Management</h2>
        {error && <p className="text-red-600">{error}</p>}
        <div className="mb-1 border rounded p-1 bg-blue-50 text-sm text-gray-700">
          <strong className="block mb-2">Rating Legend:</strong>
          <table className="table-auto w-full text-center border-collapse">
            <thead>
              <tr className="bg-blue-100">
                <th className="border px-4 py-2">0</th>
                <th className="border px-4 py-2">1</th>
                <th className="border px-4 py-2">2</th>
                <th className="border px-4 py-2">3</th>
                <th className="border px-4 py-2">4</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border px-4 py-2">No Experience</td>
                <td className="border px-4 py-2">Basic (Not Delivered training)</td>
                <td className="border px-4 py-2">Intermediate (Comfortable delivering TechGenics training)</td>
                <td className="border px-4 py-2">Advanced (Delivered TechGenics and Calendar trainings)</td>
                <td className="border px-4 py-2">Expert (Delivered complex and customized project-specific training, hold certification in this area)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end min-h-[80px]">
          <div>
            <label className="block mb-1 font-semibold">Track</label>
            <select
              value={newSkill.track}
              onChange={handleTrackChange}
              disabled={editingIndex !== null}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">-- Select Track --</option>
              {tracks.map((track, idx) => (
                <option key={idx} value={track}>{track}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 font-semibold">Skill Name</label>
            <input
              list="skills"
              value={newSkill.skillName}
              onChange={handleSkillNameChange}
              disabled={editingIndex !== null}
              className="w-full px-3 py-2 border rounded"
            />
            <datalist id="skills">
              {filteredSkillSuggestions.map((skill, idx) => (
                <option key={idx} value={skill.skillName} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="block mb-1 font-semibold">Rating</label>
            <select
              value={newSkill.rating}
              onChange={(e) => setNewSkill({ ...newSkill, rating: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="0">0 - No Experience</option>
              <option value="1">1 - Basic</option>
              <option value="2">2 - Intermediate</option>
              <option value="3">3 - Advanced</option>
              <option value="4">4 - Expert</option>
            </select>
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={handleAddOrUpdateSkill}
              className="min-w-[130px] h-[40px] px-4 py-2 bg-blue-600 text-white rounded text-sm"
            >
              {editingIndex !== null ? "Update Skill" : "Add Skill"}
            </button>
            {editingIndex !== null && (
              <button
                onClick={resetForm}
                className="min-w-[130px] h-[40px] px-4 py-2 bg-gray-500 text-white rounded text-sm"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search skill..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64 px-3 py-2 border rounded"
          />
        </div>

        
        
        <table {...getTableProps()} className="min-w-full bg-white border rounded shadow-sm">
          <thead className="bg-gray-100">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="text-left px-4 py-2 border-b font-medium text-gray-700"
                  >
                    {column.render("Header")}
                    {column.id !== "Actions" && (
                      <span className="ml-1 text-gray-400">
                        {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : "⬍"}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="hover:bg-gray-50">
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} className="px-4 py-2 border-b">
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={previousPage}
              disabled={!canPreviousPage}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={nextPage}
              disabled={!canNextPage}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>

          <div className="text-sm">
            Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
          </div>

          <div className="text-sm">
            | Go to page:{" "}
            <input
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => gotoPage(Number(e.target.value) - 1)}
              className="w-16 px-2 py-1 border rounded"
            />
          </div>

          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="px-2 py-1 border rounded"
          >
            {[5, 10, 20].map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default SMESkillManagementPage;
